import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useApi, useRouteRefParams } from '@backstage/frontend-plugin-api';
import { catalogApiRef, entityRouteRef } from '@backstage/plugin-catalog-react';
import {
  AccountEntity,
  CLUSTER_CHIP_COLORS,
  CloudResourceEntity,
  ClusterEntity,
  getAccountSyncState,
  getRuleSeverityFromResources,
  RuleEntity,
  relTime,
} from '@internal/backstage-plugin-catalog-devops-common';
import {
  AlertsChartPeriod,
  AlertsChartType,
  AlertsStatisticsResponse,
  SpmAlertInfo,
  getTimePeriodInDays,
} from '@internal/backstage-plugin-devops-spm-common';
import { spmApiRef } from '@internal/backstage-plugin-devops-spm';
import styles from './overview-page.module.css';
import pStyles from './project-overview.module.css';

const CATEGORIES = ['Security', 'Finance', 'Housekeeping'] as const;

const catAccentClass = (category: string): string => {
  if (category === 'Security') {
    return styles.catAccentSecurity;
  }
  if (category === 'Finance') {
    return styles.catAccentFinance;
  }
  return styles.catAccentHousekeeping;
};

const ruleTitle = (rule: RuleEntity) =>
  rule.metadata.title ?? rule.metadata.name;

const severityOf = (rule: RuleEntity): string =>
  getRuleSeverityFromResources(
    rule.spec.resources as Array<{ severity?: string }>,
  ) ?? 'Unknown';

const severityPillClass = (severity: string): string => {
  const normalized = severity.toLowerCase();
  if (normalized === 'critical') {
    return pStyles.pillFailed;
  }
  if (normalized === 'high') {
    return pStyles.pillWarning;
  }
  if (normalized === 'medium') {
    return pStyles.pillNeutral;
  }
  return pStyles.pillNeutral;
};

const scoreBarClass = (score: number): string => {
  if (score >= 80) {
    return pStyles.barSuccess;
  }
  if (score >= 50) {
    return pStyles.barWarning;
  }
  return pStyles.barDanger;
};

const providerClass = (provider: string): string =>
  provider.toUpperCase() === 'AZURE' ? pStyles.pvAzure : pStyles.pvAws;

const providerShort = (provider: string): string =>
  provider.toUpperCase() === 'AZURE' ? 'Az' : provider.slice(0, 3).toLowerCase();

const syncPillClass = (tone: string): string => {
  if (tone === 'success') {
    return pStyles.pillPassed;
  }
  if (tone === 'warning') {
    return pStyles.pillWarning;
  }
  return pStyles.pillNeutral;
};

const syncPillLabel = (sync: ReturnType<typeof getAccountSyncState>): string => {
  if (sync.errorCount > 0) {
    return `${sync.errorCount} error${sync.errorCount > 1 ? 's' : ''}`;
  }
  if (sync.tone === 'success') {
    return 'healthy';
  }
  return sync.label;
};

function KpiCard(props: {
  to: string;
  label: string;
  value: ReactNode;
  sub: string;
}) {
  const { to, label, value, sub } = props;
  return (
    <Link to={to} className={`${styles.card} ${styles.kpi}`}>
      <span className={styles.k}>{label}</span>
      <span className={styles.v}>{value}</span>
      <span className={styles.sub}>{sub}</span>
    </Link>
  );
}

/** Daily alert totals summed across the project's accounts. */
function AlertsMiniChart({ statistics }: { statistics: AlertsStatisticsResponse[] }) {
  const byDate = new Map<string, number>();
  statistics.forEach(row => {
    byDate.set(row.date, (byDate.get(row.date) ?? 0) + row.totalAlertsCount);
  });
  const tail = [...byDate.entries()].slice(-14);
  const max = Math.max(1, ...tail.map(([, count]) => count));
  return (
    <div className={styles.alChart}>
      {tail.map(([date, count], index) => (
        <div
          key={date}
          className={`${styles.alCol} ${index === tail.length - 1 ? styles.alColCur : ''}`}
          title={`${date}: ${count} total`}
        >
          <i style={{ height: `${Math.max(8, (count / max) * 100)}%` }} />
          <span>{date.slice(-2)}</span>
        </div>
      ))}
      {tail.length === 0 && (
        <div className={styles.cardSub}>No alerts data for this period</div>
      )}
    </div>
  );
}

const ProjectOverviewContent = () => {
  const { name } = useRouteRefParams(entityRouteRef);
  const catalogApi = useApi(catalogApiRef);
  const spmApi = useApi(spmApiRef);

  const [accounts, setAccounts] = useState<AccountEntity[]>([]);
  const [rules, setRules] = useState<RuleEntity[]>([]);
  const ruleByRuleId = useMemo(
    () => new Map(rules.map(rule => [rule.metadata.name, rule])),
    [rules],
  );
  const [resources, setResources] = useState<CloudResourceEntity[]>([]);
  const [clusters, setClusters] = useState<ClusterEntity[]>([]);
  const [clusterResourceCounts, setClusterResourceCounts] = useState<Map<string, number>>(
    new Map(),
  );
  const [activeFindings, setActiveFindings] = useState<number | undefined>(undefined);
  const [statistics, setStatistics] = useState<AlertsStatisticsResponse[]>([]);
  const [recentAlerts, setRecentAlerts] = useState<(SpmAlertInfo & { account?: string })[]>([]);

  const accountNames = useMemo(() => accounts.map(account => account.metadata.name), [accounts]);
  const accountTitleById = useMemo(
    () => new Map(accounts.map(a => [a.metadata.name, a.metadata.title ?? a.metadata.name])),
    [accounts],
  );

  useEffect(() => {
    let cancelled = false;
    catalogApi
      .getEntities({ filter: { kind: 'Account', 'spec.domain': name } })
      .then(({ items }) => {
        if (!cancelled) {
          setAccounts(items as AccountEntity[]);
        }
      })
      .catch(() => {
        // The page stays usable with empty aggregates.
      });
    catalogApi
      .getEntities({ filter: { kind: 'Cluster', 'spec.domain': name } })
      .then(({ items }) => {
        if (!cancelled) {
          setClusters(items as ClusterEntity[]);
        }
      })
      .catch(() => {
        // Clusters card hides without data.
      });
    return () => {
      cancelled = true;
    };
  }, [catalogApi, name]);

  useEffect(() => {
    if (accountNames.length === 0) {
      return () => {};
    }
    let cancelled = false;
    const perAccountFilter = accountNames.map(accountName => ({
      kind: 'Rule',
      'spec.account': accountName,
    }));
    catalogApi
      .getEntities({ filter: perAccountFilter })
      .then(({ items }) => {
        if (!cancelled) {
          setRules(items as RuleEntity[]);
        }
      })
      .catch(() => {
        // Compliance snapshot degrades to empty.
      });
    catalogApi
      .getEntities({
        filter: accountNames.map(accountName => ({
          kind: 'CloudResource',
          'spec.account': accountName,
        })),
      })
      .then(({ items }) => {
        if (!cancelled) {
          setResources(items as CloudResourceEntity[]);
        }
      })
      .catch(() => {
        // Inventory numbers degrade to zero.
      });
    catalogApi
      .getEntities({
        filter: accountNames.map(accountName => ({
          kind: 'Insight',
          'spec.account': accountName,
        })),
      })
      .then(({ items }) => {
        if (cancelled) {
          return;
        }
        setActiveFindings(
          items.filter(
            insight => (insight.spec as { status?: string }).status === 'Active',
          ).length,
        );
      })
      .catch(() => {
        // Insights card hides when catalog has no analyzer data.
      });
    return () => {
      cancelled = true;
    };
  }, [catalogApi, accountNames]);

  useEffect(() => {
    if (clusters.length === 0) {
      return () => {};
    }
    let cancelled = false;
    catalogApi
      .getEntities({ filter: { kind: 'ClusterResource' } })
      .then(({ items }) => {
        if (cancelled) {
          return;
        }
        const counts = new Map<string, number>();
        items.forEach(item => {
          const cluster = (item.spec as { cluster?: string }).cluster;
          if (cluster) {
            counts.set(cluster, (counts.get(cluster) ?? 0) + 1);
          }
        });
        setClusterResourceCounts(counts);
      })
      .catch(() => {
        // Resource counts stay empty.
      });
    return () => {
      cancelled = true;
    };
  }, [catalogApi, clusters]);

  // SPM per account, merged client-side (few accounts per project).
  useEffect(() => {
    if (accountNames.length === 0) {
      return () => {};
    }
    let cancelled = false;
    Promise.all(
      accountNames.map(async accountName => {
        const [accountStatistics] = await spmApi
          .getAlertStatistics(
            accountName,
            getTimePeriodInDays(AlertsChartPeriod.LAST_MONTH),
            1,
            [AlertsChartType.TOTAL],
          )
          .catch(() => [[] as AlertsStatisticsResponse[]]);
        const recent = await spmApi
          .getRecentAlerts(accountName, 3)
          .catch(() => [] as SpmAlertInfo[]);
        return { accountName, statistics: accountStatistics, recent };
      }),
    ).then(batches => {
      if (cancelled) {
        return;
      }
      setStatistics(batches.flatMap(batch => batch.statistics));
      setRecentAlerts(
        batches
          .flatMap(batch =>
            batch.recent.map(alert => ({ ...alert, account: batch.accountName })),
          )
          .sort((a, b) => (b.created_at ?? '').localeCompare(a.created_at ?? ''))
          .slice(0, 5),
      );
    });
    return () => {
      cancelled = true;
    };
  }, [spmApi, accountNames]);

  const perAccount = useMemo(
    () =>
      accounts.map(account => {
        const accountRules = rules.filter(
          rule => rule.spec.account === account.metadata.name,
        );
        const scored = accountRules.filter(rule => rule.spec.result !== 'Unknown');
        const passed = scored.filter(rule => rule.spec.result === 'Passed').length;
        const failed = accountRules.filter(rule => rule.spec.result === 'Failed');
        return {
          account,
          resources: resources.filter(
            resource => resource.spec.account === account.metadata.name,
          ).length,
          score: scored.length ? Math.round((passed / scored.length) * 100) : 0,
          failedCount: failed.length,
          sync: getAccountSyncState(account),
        };
      }),
    [accounts, rules, resources],
  );

  const compliance = useMemo(() => {
    const scored = rules.filter(rule => rule.spec.result !== 'Unknown');
    const passed = scored.filter(rule => rule.spec.result === 'Passed').length;
    const failed = rules.filter(rule => rule.spec.result === 'Failed');
    const byCategory = CATEGORIES.map(category => {
      const rows = rules.filter(rule => rule.spec.category === category);
      const passedInCategory = rows.filter(rule => rule.spec.result === 'Passed').length;
      return { category, passed: passedInCategory, total: rows.length };
    }).filter(row => row.total > 0);
    const topFailed = failed
      .sort((a, b) => b.spec.resources.length - a.spec.resources.length)
      .slice(0, 3);
    return {
      score: scored.length ? Math.round((passed / scored.length) * 100) : 0,
      failedCount: failed.length,
      rulesTotal: rules.length,
      unknownCount: rules.length - scored.length,
      byCategory,
      topFailed,
    };
  }, [rules]);

  const inventory = useMemo(() => {
    const regions = new Set<string>();
    resources.forEach(resource => regions.add(resource.spec?.region ?? ''));
    regions.delete('');
    const providers = [
      ...new Set(accounts.map(account => (account.spec.type ?? '').toUpperCase())),
    ].filter(Boolean);
    const aws = accounts.filter(account => (account.spec.type ?? '').toUpperCase() !== 'AZURE').length;
    return {
      total: resources.length,
      regions: regions.size,
      providers,
      awsCount: aws,
      azureCount: accounts.length - aws,
    };
  }, [resources, accounts]);

  const errorAccounts = accounts.filter(
    account => getAccountSyncState(account).errorCount > 0,
  );
  const projectHref = `/catalog/default/domain/${name}`;

  return (
    <>
      {/* KPI band */}
      <div className={styles.kpiBand}>
        <KpiCard
          to={`${projectHref}/cloud-resources`}
          label="Inventory"
          value={
            <>
              {inventory.total}
              <small>resources</small>
            </>
          }
          sub={`${accounts.length} accounts · ${inventory.awsCount} AWS + ${inventory.azureCount} Azure · ${inventory.regions} regions`}
        />
        <KpiCard
          to={`${projectHref}/compliance`}
          label="Compliance score"
          value={
            <>
              {compliance.score}
              <small>/100</small>
            </>
          }
          sub={`Unknown rules excluded · per-account: ${perAccount.map(row => row.score).join(' / ') || '—'}`}
        />
        <KpiCard
          to={`${projectHref}/compliance?st=Failed`}
          label="Failed rules"
          value={
            <>
              {compliance.failedCount}
              <small>
                of {compliance.rulesTotal}
                {compliance.unknownCount > 0
                  ? ` · ${compliance.unknownCount} unknown`
                  : ''}
              </small>
            </>
          }
          sub={`across ${accounts.length} accounts`}
        />
      </div>

      {/* scan-error alert (only when accounts actually have errors) */}
      {errorAccounts.length > 0 && (
        <div className={styles.alert}>
          <span aria-hidden="true">⚠</span>
          <span>
            {errorAccounts
              .map(account => account.metadata.title ?? account.metadata.name)
              .join(', ')}{' '}
            finished their last scan with errors.
          </span>
          <Link to={`${projectHref}/accounts`}>View accounts</Link>
        </div>
      )}

      {/* row: accounts centerpiece + clusters + compliance snapshot; the
          clusters column exists only when the project actually has clusters */}
      <div
        className={`${pStyles.row3} ${clusters.length === 0 ? pStyles.row3NoClusters : ''}`}
      >
        <div className={`${styles.card} ${styles.cardH}`}>
          <div className={styles.cardTitle}>
            Accounts
            <Link className={styles.go} to={`${projectHref}/accounts`}>
              Open Accounts →
            </Link>
          </div>
          <div className={styles.cardSub}>
            Compliance and inventory roll up from each account; click an
            account to drill in
          </div>
          <table className={pStyles.accTable} aria-label="Accounts in this project">
            <thead>
              <tr>
                <th>Account</th>
                <th className={pStyles.num}>Resources</th>
                <th>Compliance</th>
                <th className={pStyles.num}>Failed rules</th>
                <th>Sync</th>
              </tr>
            </thead>
            <tbody>
              {perAccount.map(row => {
                const provider = (row.account.spec.type ?? '').toUpperCase();
                return (
                  <tr key={row.account.metadata.name}>
                    <td>
                      <Link
                        className={pStyles.accName}
                        to={`/catalog/default/account/${row.account.metadata.name}`}
                      >
                        <span className={`${pStyles.pv} ${providerClass(provider)}`}>
                          {providerShort(provider)}
                        </span>
                        {row.account.metadata.title ?? row.account.metadata.name}
                        <span className={pStyles.id}>
                          {row.account.metadata.name}
                        </span>
                      </Link>
                    </td>
                    <td className={pStyles.num}>{row.resources}</td>
                    <td>
                      <span className={pStyles.scorePill}>
                        <span className={pStyles.bar}>
                          <i
                            className={scoreBarClass(row.score)}
                            style={{ width: `${row.score}%` }}
                          />
                        </span>
                        {row.score}
                      </span>
                    </td>
                    <td className={pStyles.num}>
                      {row.failedCount > 0 ? (
                        <span className={`${pStyles.pill} ${pStyles.pillFailed}`}>
                          {row.failedCount}
                        </span>
                      ) : (
                        <span className={pStyles.muted}>0</span>
                      )}
                    </td>
                    <td>
                      <span className={`${pStyles.pill} ${syncPillClass(row.sync.tone)}`}>
                        <span className={pStyles.pillDot} />
                        {syncPillLabel(row.sync)}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {perAccount.length === 0 && (
                <tr>
                  <td colSpan={5} className={pStyles.muted}>
                    No accounts in this project yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {clusters.length > 0 && (
          <div className={`${styles.card} ${styles.cardH}`}>
            <div className={styles.cardTitle}>Clusters</div>
            <div className={styles.cardSub}>
              EKS / AKS / K8s across the project's accounts
            </div>
            <div style={{ marginTop: 8 }}>
              {clusters.map(cluster => (
                <div key={cluster.metadata.name} className={pStyles.clRow}>
                  <span
                    className={pStyles.tchip}
                    style={{
                      background:
                        CLUSTER_CHIP_COLORS[
                          (cluster.spec.type ?? 'K8S').toUpperCase()
                        ] ?? '#7c5cbf',
                    }}
                  >
                    {(cluster.spec.type ?? 'K8S').toUpperCase()}
                  </span>
                  <Link
                    className={pStyles.accName}
                    to={`/catalog/default/cluster/${cluster.metadata.name}`}
                  >
                    {cluster.metadata.title ?? cluster.metadata.name}
                  </Link>
                  <span className={pStyles.clMeta}>{cluster.spec.account}</span>
                  <span className={pStyles.clRes}>
                    {clusterResourceCounts.get(cluster.metadata.name) ?? 0} res
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className={`${styles.card} ${styles.cardH}`}>
          <div className={styles.cardTitle}>
            Compliance snapshot
            <Link className={styles.go} to={`${projectHref}/compliance`}>
              Open Compliance →
            </Link>
          </div>
          <div className={styles.cardSub}>
            Aggregated across all accounts
          </div>
          <div className={styles.catBars} style={{ marginTop: 12 }}>
            {compliance.byCategory.map(row => {
              const pct = row.total
                ? Math.round((row.passed / row.total) * 100)
                : 0;
              return (
                <Link
                  key={row.category}
                  className={styles.catBar}
                  to={`${projectHref}/compliance?cat=${row.category}`}
                >
                  <span className={styles.nm}>{row.category}</span>
                  <span className={styles.bar}>
                    <i
                      className={catAccentClass(row.category)}
                      style={{ width: `${pct}%` }}
                    />
                  </span>
                  <span className={styles.pc}>
                    {row.passed}/{row.total}
                  </span>
                </Link>
              );
            })}
            {compliance.byCategory.length === 0 && (
              <div className={styles.cardSub}>No rules ingested yet</div>
            )}
          </div>
          <div className={pStyles.topRules}>
            {compliance.topFailed.map(rule => (
              <div key={rule.metadata.name} className={pStyles.topRule}>
                <span className={`${pStyles.pill} ${pStyles.pillFailed}`}>
                  <span className={pStyles.pillDot} />
                  Failed
                </span>
                <span
                  className={`${pStyles.pill} ${severityPillClass(severityOf(rule))}`}
                >
                  <span className={pStyles.pillDot} />
                  {severityOf(rule)}
                </span>
                <Link
                  className={pStyles.nm}
                  to={`${projectHref}/compliance?st=Failed`}
                >
                  {ruleTitle(rule)}
                </Link>
                <span className={pStyles.code}>{rule.spec.type}</span>
                <span className={pStyles.aff}>
                  {rule.spec.resources.length} res
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* row: alerts + insights */}
      <div className={styles.row2b}>
        <div className={`${styles.card} ${styles.cardH}`}>
          <div className={styles.cardTitle}>Alerts</div>
          <AlertsMiniChart statistics={statistics} />
          <div className={styles.alList}>
            {recentAlerts.map((alert, index) => {
              const rule = ruleByRuleId.get(alert.ruleId);
              const title = rule?.metadata.title ?? alert.metadata?.type ?? alert.ruleId;
              return (
                <div key={`${alert.id}-${index}`} className={styles.alItem}>
                  <span className={styles.nm}>{title}</span>
                  <span
                    className={pStyles.alAcc}
                    title={alert.account}
                  >
                    {alert.account
                      ? accountTitleById.get(alert.account) ?? alert.account
                      : '—'}
                  </span>
                  <span className={styles.when}>{relTime(alert.created_at)}</span>
                </div>
              );
            })}
            {recentAlerts.length === 0 && (
              <div className={styles.cardSub}>No recent alerts</div>
            )}
          </div>
        </div>

        <div className={`${styles.card} ${styles.cardH}`}>
          <div className={styles.cardTitle}>Insights</div>
          {activeFindings !== undefined ? (
            <div className={styles.insRow}>
              <span
                className={`${styles.insRowOk} ${activeFindings === 0 ? '' : styles.insRowOkWarn}`}
              >
                {activeFindings === 0 ? '✓' : '!'}
              </span>
              <span className={styles.nm}>
                External access analyzers
                <div className={styles.cardSub}>
                  {activeFindings === 0
                    ? 'No findings in any account'
                    : `${activeFindings} active finding${activeFindings > 1 ? 's' : ''}`}
                </div>
              </span>
              <span className={pStyles.alAcc}>{accounts.length} accounts</span>
            </div>
          ) : (
            <div className={styles.cardSub}>No analyzer data yet</div>
          )}
        </div>
      </div>
    </>
  );
};

export const ProjectOverviewTab = () => {
  return <ProjectOverviewContent />;
};
